/** Posts a bill now, or parks it in the retry queue when the API is unreachable. */
import type { PosTransactionRequest, PosTransactionResult } from "./api-types";
import { ApiHttpError, NetworkError, staffApi } from "./staff-api-client";
import { drainQueue, enqueueTransaction } from "./transaction-retry-queue";

export type SubmitOutcome = { queued: false; result: PosTransactionResult } | { queued: true; result: null };

/**
 * One Idempotency-Key per bill: generated here and reused for every retry from the queue.
 * Non-retryable API errors (4xx) are rethrown so staff can correct the bill.
 */
export async function submitTransaction(request: PosTransactionRequest): Promise<SubmitOutcome> {
  const idempotencyKey = crypto.randomUUID();
  if (!navigator.onLine) {
    await enqueueTransaction(idempotencyKey, request);
    return { queued: true, result: null };
  }
  try {
    return { queued: false, result: await staffApi.postTransaction(request, idempotencyKey) };
  } catch (error) {
    if (error instanceof NetworkError || (error instanceof ApiHttpError && error.retryable)) {
      await enqueueTransaction(idempotencyKey, request);
      return { queued: true, result: null };
    }
    throw error;
  }
}

/** Sends everything waiting in the queue (startup, `online` event, timer, manual button). */
export function flushQueue(): Promise<void> {
  return drainQueue((request, key) => staffApi.postTransaction(request, key));
}
